/**
 * sinPulse — radial sine ripple, pumping inward on bass, magenta/violet seed.
 *
 * Uses the `sinusoidal` warp kind. The ripple amplitude tracks mid energy;
 * the ripple frequency wobbles with the bass envelope so kicks feel like
 * underwater pings rather than just zooms.
 */

import { WarpKind, type VisualizerPreset } from '../presetTypes';

export const sinPulsePreset: VisualizerPreset = {
  id: 'sinPulse',
  name: 'Sin Pulse',
  seed: {
    kind: 'waveform',
    params: {
      baseY: 0.0,
      gain: 0.5,
      halfThick: 0.013,
      intensity: 0.72,
      color: [0.95, 0.45, 1.0], // magenta
      edgeSharp: 8.0,
    },
  },
  warp: {
    kind: WarpKind.Sinusoidal,
    params: {
      baseZoom: 0.01,
      bassZoomGain: 0.03,
      rotation: 0,
      bassRotationGain: 0,
      ripple: 0.0045,
      rippleFreq: 8.0,
      decay: 0.978,
      chroma: 0.004,
      hueShift: 0.02,
      beatPunch: 0.06,
      centerDriftAmp: 0.07,
      centerDriftRate: 0.21,
    },
  },
  modulation: [
    {
      target: 'warp.ripple',
      source: 'mid',
      transform: 'linear',
      amount: 0.005,
      smoothingMs: 60,
    },
    {
      target: 'warp.rippleFreq',
      source: 'bass',
      transform: 'smoothPulse',
      amount: 3.5,
      smoothingMs: 150,
    },
    {
      target: 'seed.intensity',
      source: 'rms',
      transform: 'linear',
      amount: 0.6,
      smoothingMs: 80,
    },
    {
      target: 'warp.chroma',
      source: 'beatConfidence',
      transform: 'smoothPulse',
      amount: 0.004,
      smoothingMs: 220,
    },
  ],
  transition: { durationMs: 850 },
  present: {
    bloomEnabled: true,
    // Bright magenta seed line + radial sine ripples produce sharp
    // highlights at the wave crests; lift the threshold so only the
    // crests glow, with moderate intensity to feel like underwater pings.
    bloomThreshold: 0.9,
    bloomKnee: 0.2,
    bloomSpread: 1.2,
    bloomIntensity: 0.7,
    vignette: 0.2,
    grain: 0.012,
  },
};
